import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';

import { ListTableNavigation } from '~/modules/common/components/ListTable/components/DataTable/components';
import DrilldownTableFooter from './DrilldownTableFooter';
import DrilldownTableHeader from './DrilldownTableHeader';
import DrilldownTableRow from './DrilldownTableRow';
import DrilldownTableSkeletonRows from './DrilldownTableSkeletonRows';
import DrilldownTableOutOfBoundsRow from './DrilldownTableOutOfBoundsRow';

const useStyles = makeStyles(theme => ({
  noMatchMessage: {
    padding: theme.spacing(3, 2),
    color: theme.palette.text.secondary
  },
  table: {
    backgroundColor: theme.palette.grey[100],
    alignSelf: 'flex-start',
    width: 'unset',
    minWidth: '100%',
    borderCollapse: 'separate',
    margin: 0,
    padding: 0,
    border: 'none'
  },
  tbody: {
    '&:first-of-type $groupHeaderRowCell': {
      borderTop: 'none'
    }
  },

  tableHeader: {},
  tableHeaderRow: {},
  tableHeaderCellFirst: {},
  tableHeaderCellFirstFrozen: {},
  tableHeaderCell: {},
  tableHeaderCellLast: {},
  tableHeaderCellLastFrozen: {},

  row: {},
  cell: {},
  cellFirst: {},
  cellFirstFrozen: {},
  cellLast: {},
  cellLastFrozen: {},

  groupHeaderRow: {},
  groupHeaderRowCell: {},

  tableFooter: {},
  tableFooterRow: {},
  tableFooterCellFirst: {},
  tableFooterCellFirstFrozen: {},
  tableFooterCell: {},
  tableFooterCellLast: {},
  tableFooterCellLastFrozen: {},

  disableGutters: {}
}));

const DrilldownTable = ({
  columns,
  items,
  totals,
  noItemsMessage,
  onRowClick,
  canView = true,
  rowIdKey = 'slug',
  onSortChange,
  sort,
  showPagination = false,
  pageSize,
  hasMoreRows = false,
  loading = false,
  currentPage,
  setCurrentPage
}) => {
  const classes = useStyles();

  const hasItems = Boolean(items && items.length > 0);
  const outOfBounds =
    showPagination && !loading && !hasItems && currentPage > 1;

  if (!loading && !outOfBounds && !hasItems) {
    return <div className={classes.noMatchMessage}>{noItemsMessage}</div>;
  }

  const paginationProps = {
    currentPage,
    navigatePage: setCurrentPage,
    pageSize,
    recordCount: items.length
  };

  const headerPaginationComponent = showPagination && (
    <ListTableNavigation {...paginationProps} />
  );
  const isOnlyPage = currentPage === 1 && !hasMoreRows;
  const footerPaginationComponent = showPagination && !isOnlyPage && (
    <ListTableNavigation {...paginationProps} />
  );

  return (
    <Table className={classes.table} role="table">
      <DrilldownTableHeader
        columns={columns}
        onSortChange={onSortChange}
        sort={sort}
        paginationComponent={headerPaginationComponent}
      />
      {!loading && !outOfBounds && totals && (
        <DrilldownTableFooter
          columns={columns}
          totals={totals}
          paginationComponent={footerPaginationComponent}
        />
      )}
      <TableBody role="rowgroup" className={classes.tbody}>
        {loading ? (
          <DrilldownTableSkeletonRows columns={columns} />
        ) : outOfBounds ? (
          <DrilldownTableOutOfBoundsRow
            columns={columns}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        ) : (
          items.map(item => (
            <DrilldownTableRow
              key={item.id}
              columns={columns}
              item={item}
              onClick={onRowClick}
              canView={canView}
              idKey={rowIdKey}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
};

DrilldownTable.propTypes = {
  columns: PropTypes.array.isRequired,
  items: PropTypes.array,
  totals: PropTypes.object,
  noItemsMessage: PropTypes.node.isRequired,
  onRowClick: PropTypes.func,
  canView: PropTypes.bool,
  rowIdKey: PropTypes.string,
  onSortChange: PropTypes.func,
  sort: PropTypes.object,
  showPagination: PropTypes.bool,
  pageSize: PropTypes.number,
  hasMoreRows: PropTypes.bool,
  loading: PropTypes.bool,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func
};

export default DrilldownTable;
